import Box from "@mui/material/Box";
import CssBaseline from "@mui/material/CssBaseline";
import { styled, type Theme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useDialog } from "_shared/_providers/dialogs/dialog";
import { useAttorneyHubDispatch } from "_shared/_stores/redux-store";
import Footer from "_shared/_views/footers/footer";
import { PageFooterPlacementProvider, usePagePlacesFooterAnswer } from "_shared/_views/footers/page-footer-placement";
import Header from "_shared/_views/headers/header";
import { SHELL_BOTTOM_BAR_HEIGHT_PROPERTY } from "_shared/_views/shell-bottom-bar";
import { useShellTopBannersHeight } from "_shared/_views/shell-top-banners";
import SideNav from "_shared/_views/side-navs/side-nav";
import {
  AlertType,
  SystemNotificationBanner,
} from "_shared/_views/system-notification-banner/system-notification-banner";
import { useSystemNotificationBanner } from "_shared/_providers/banner/system-notification-banner-provider";
import { IS_E2E } from "_shared/_utilities/meta";
import SessionRecordingConsentBanner from "_shared/session-recording/_views/session-recording-consent-banner";
import AiAssistant from "ai-assistant/_views/ai-assistant";
import { AssistantDockProvider, useAssistantDock } from "ai-assistant/dock/assistant-dock";
import { ASSISTANT_BAR_HEIGHT_PX, AssistantBar, AssistantNavEntry } from "ai-assistant/dock/assistant-launchers";
import { ASSISTANT_DEFAULT_WIDTH } from "ai-assistant/dock/assistant-column-width";
import AssistantResizeHandle, { ASSISTANT_WIDTH_PROPERTY } from "ai-assistant/dock/assistant-resize-handle";
import { AssistantScreenContextProvider } from "ai-assistant/screen-context/assistant-screen-context-provider";
import Company from "marketplace/companies/company";
import CompanyProfile from "marketplace/companies/profiles/company-profile";
import { addIndividual } from "marketplace/individuals/_stores/individuals-redux-slice";
import Individual from "marketplace/individuals/individual";
import React, { useEffect } from "react";
import axios from "axios";
import AttorneyHubAPIService from "_shared/_api/attorney-hub-api-service";
import { Outlet, useLocation } from "react-router";
import { useSession } from "users/__session/session-context";
import { parseSystemMessageSeverity, SystemMessageSeverity } from "notifications/system-notifications/system-message-severity";
import useSystemMessagesRefresh from "_shared/_views/use-system-messages-refresh";

const Root = styled(Box)(() => ({
  display: "flex",
  flexDirection: "column",
  height: "100vh",
}));
/** Everything above the app bar, measured as one so a drawer's paper can start below the app bar. */
const TopBanners = styled("div")(() => ({
  flexShrink: 0,
}));
const SubRoot = styled(Box)(() => ({
  alignItems: "stretch",
  display: "flex",
  flexDirection: "row",
  height: "100%",
  overflow: "hidden",
  position: "relative",
  width: "100%",
}));
/**
 * Where the assistant docks (#6128): beside an icon navigation rail at `lg` and up, in a column of its own
 * beside the page between `md` and `lg`, and over the whole window below `md`, where a page beside it
 * would be too narrow to read. Drawn whether or not the assistant is open, so its conversation is kept
 * while it is closed; `display: none` is what closing it does.
 *
 * Beside the rail a user can drag it wider or narrower (#7205); the handle writes the width it settles
 * on into a custom property on the column, so a drag re-renders nothing.
 */
type AssistantDockLayout = "nav" | "column" | "window";
const assistantColumnWidths: Record<AssistantDockLayout, string> = {
  nav: `var(${ASSISTANT_WIDTH_PROPERTY}, ${ASSISTANT_DEFAULT_WIDTH}px)`,
  column: "360px",
  window: "100%",
};
const AssistantColumn = styled("div", {
  shouldForwardProp: (prop) => prop !== "open" && prop !== "layout",
})<{ open: boolean; layout: AssistantDockLayout }>(({ theme, open, layout }) => ({
  [theme.breakpoints.down("sm")]: {
    paddingTop: theme.spacing(7),
  },
  boxSizing: "border-box",
  display: open ? "flex" : "none",
  flexDirection: "column",
  flexShrink: 0,
  height: "100%",
  minHeight: 0,
  paddingTop: theme.spacing(8),
  // For the resize handle, which lies over its edge.
  position: "relative",
  width: assistantColumnWidths[layout],
}));
const MainContainer = styled("main", {
  shouldForwardProp: (prop) => prop !== "behindAssistant",
})<{ behindAssistant: boolean }>(({ theme, behindAssistant }) => ({
  [theme.breakpoints.down("sm")]: {
    padding: theme.spacing(7, 0, 0, 0),
  },
  // Hidden rather than unmounted while the assistant fills the window, so the page is as the user
  // left it when they close the assistant.
  display: behindAssistant ? "none" : "flex",
  flexDirection: "column",
  flexGrow: 1,
  overflow: "hidden",
  padding: theme.spacing(8, 0, 0, 0),
  width: "100%",
}));
const PageContent = styled("section")(() => ({
  flexGrow: 1,
  overflow: "hidden",
}));

export default function Main() {
  const session = useSession();
  const userId = session.user?.id?.toString();

  // Keyed on the user, so whoever signs in next starts from their own choice to keep it open.
  return (
    <AssistantDockProvider key={userId ?? "signed-out"} userId={userId}>
      <PageFooterPlacementProvider>
        <AppShell />
      </PageFooterPlacementProvider>
    </AssistantDockProvider>
  );
}

function AppShell() {
  const location = useLocation();
  const hasNavColumn = useMediaQuery((theme: Theme) => theme.breakpoints.up("lg"));
  const assistantFillsWindow = useMediaQuery((theme: Theme) => theme.breakpoints.down("md"));
  const dock = useAssistantDock();
  // A page with a column beside its content draws the footer in its own layout (#6911).
  const pagePlacesFooter = usePagePlacesFooterAnswer();

  const prevPathRef = React.useRef(location.pathname);

  const [showNav, setShowNav] = React.useState(false);
  const [topBanners, setTopBanners] = React.useState<HTMLDivElement | null>(null);
  useShellTopBannersHeight(topBanners);
  const { banners, clearBanner, dismissSystemBanner, setSystemBanners } = useSystemNotificationBanner();

  const session = useSession();
  const restrictedOnboarding = session.isRestrictedOnboarding;
  const authToken = session.authToken?.value;
  const userId = session.user?.id?.toString();
  const dispatch = useAttorneyHubDispatch();
  const { numDialogs, closeDialog } = useDialog();

  const dismissPersistedBanner = React.useCallback(async (id: string) => {
    if (authToken) {
      await axios.post(new URL(`/system-messages/${id}/dismiss`, AttorneyHubAPIService.apiBaseUrl).toString(), {}, {
        headers: { Authorization: `Bearer ${authToken}` },
      }).catch(() => undefined);
    }
    dismissSystemBanner(id);
  }, [authToken, dismissSystemBanner]);

  const loadActiveSystemMessages = React.useCallback(() => {
    if (!userId || !authToken) return Promise.resolve();
    const headers = { Authorization: `Bearer ${authToken}` };
    return axios.get(new URL("/system-messages/active", AttorneyHubAPIService.apiBaseUrl).toString(), { headers })
      .then(({ data }) => {
        const active = (data as Array<{ id: string; message: string; severity: unknown }>)
          .map((item) => {
            const severity = parseSystemMessageSeverity(item.severity) ?? SystemMessageSeverity.Info;
            return {
              id: item.id,
              message: item.message,
              type: severity,
            };
          });
        setSystemBanners(active);
      })
      .catch(() => undefined);
  }, [authToken, setSystemBanners, userId]);

  useSystemMessagesRefresh(userId, loadActiveSystemMessages);

  // Close all dialogs when the route changes
  useEffect(() => {
    const prevPath = prevPathRef.current;
    const nextPath = location.pathname;

    if (!location.state?.exemptFromClose && prevPath !== nextPath && numDialogs > 0) {
      closeDialog();
    }

    prevPathRef.current = nextPath;
  }, [location.pathname, numDialogs, closeDialog, location.state?.exemptFromClose]);

  // On initial mount
  useEffect(() => {
    if (session.user?.individualId && session.user.id && session.user.companyId && session.user.legalEntityId) {
      const userIndividual = Individual.create({
        id: session.user.individualId,
        userId: session.user.id,
        legalEntityId: session.user.legalEntityId,
        isVisible: true,
        // This seed wins over the fetched record for the rest of the session — `addIndividual` never
        // overwrites an existing `byUserId` entry — so it has to carry signing authority too. Without
        // it every reader of the cache saw the signed-in user as unable to sign, which is what the
        // Client Representative picker gates on (issue #3786).
        hasSigningAuthority: session.user.isCompanyManager,
        profile: {
          userId: session.user.id,
          firstName: session.user.name.firstName ?? "",
          lastName: session.user.name.lastName ?? "",
        },
        company: Company.create({
          id: session.user.companyId,
          legalEntityId: session.user.legalEntityId,
          profile: CompanyProfile.create({
            name: session.user.companyName ?? "",
            categories: [],
          }),
        }),
      });
      dispatch(addIndividual(userIndividual));
    }
  }, [
    dispatch,
    session.user?.companyId,
    session.user?.companyName,
    session.user?.id,
    session.user?.individualId,
    session.user?.isCompanyManager,
    session.user?.legalEntityId,
    session.user?.name.firstName,
    session.user?.name.lastName,
  ]);

  // The nav is a column at `lg` and up, unless the open-entity-agreement gate hides it. There the
  // assistant docks in its place and is opened from the top of it; everywhere else, from a bar at the
  // bottom of the window (#6128).
  const showsSideNav = !session.openEntityAgreements.length;
  const opensFromNav = hasNavColumn && showsSideNav;
  const showsBar = !opensFromNav && !dock.isOpen;
  let dockLayout: AssistantDockLayout = "column";
  if (hasNavColumn) dockLayout = "nav";
  else if (assistantFillsWindow) dockLayout = "window";
  const shellStyle = {
    [SHELL_BOTTOM_BAR_HEIGHT_PROPERTY]: showsBar ? `${ASSISTANT_BAR_HEIGHT_PX}px` : "0px",
  } as React.CSSProperties;

  return (
    <Root style={shellStyle}>
      <CssBaseline />
      <TopBanners ref={setTopBanners}>
        {banners.map((banner) => (
          <SystemNotificationBanner key={banner.id ?? "outage"} message={banner.message} type={banner.type ?? AlertType.Info}
            onClose={banner.id ? () => void dismissPersistedBanner(banner.id!) : clearBanner} />
        ))}
        {/* The functional tests run without FullStory, so there is nothing to ask them about. */}
        {!IS_E2E && <SessionRecordingConsentBanner />}
      </TopBanners>
      <AssistantScreenContextProvider>
        <SubRoot>
          <Header
            toggleNavMenuClicked={() => setShowNav(!showNav)}
            hideControls={session.openEntityAgreements.length > 0}
            restrictedOnboarding={restrictedOnboarding}
          />
          {/* The docked assistant leaves the navigation available as an icon rail. */}
          {showsSideNav && (
            <SideNav
              compact={hasNavColumn && dock.isOpen}
              showNav={showNav}
              navMenuClosed={() => setShowNav(false)}
              leading={opensFromNav && !dock.isOpen ? <AssistantNavEntry /> : undefined}
            />
          )}
          <AssistantColumn open={dock.isOpen} layout={dockLayout}>
            <AiAssistant />
            {dock.isOpen && dockLayout === "nav" && <AssistantResizeHandle key={userId} userId={userId} />}
          </AssistantColumn>
          <MainContainer behindAssistant={dock.isOpen && dockLayout === "window"}>
            <PageContent>
              <Outlet />
            </PageContent>
            {!pagePlacesFooter && <Footer />}
          </MainContainer>
        </SubRoot>
        {showsBar && <AssistantBar />}
      </AssistantScreenContextProvider>
    </Root>
  );
}
